import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "@/utils/apiConfig";
import { ChevronRight, X, MapPin, Users, MessageCircle, Fish, Compass, Vote, MessageSquare, FileText } from "lucide-react";

const API = `${BACKEND_URL}/api`;

const WALKTHROUGH_STEPS = [
  {
    target: '[data-testid="logo"]',
    title: "Welcome to George Dives!",
    description: "Let's walk you through the site. We'll visit each section so you know where everything is.",
    icon: Compass,
    position: "bottom",
    navigateTo: "/"
  },
  {
    target: '[data-testid="nav-map"]',
    title: "Dive Map",
    description: "Explore dive sites and dive centers around the world on our interactive map. Click to browse locations.",
    icon: MapPin,
    position: "bottom",
    navigateTo: "/map"
  },
  {
    target: ".leaflet-container",
    title: "Interactive Map",
    description: "Dive sites and centres are pinned here. Click any marker to see details, photos, and reviews from the community.",
    icon: MapPin,
    position: "top-center",
    navigateTo: "/map",
    fallbackPosition: { top: 200, left: 100 }
  },
  {
    target: '[data-testid="nav-community"]',
    title: "Community Hub",
    description: "Your home base for group trips, discussions, and marine life records. Let's take a look inside.",
    icon: Users,
    position: "bottom",
    navigateTo: "/community"
  },
  {
    target: '[data-testid="trip-voting-section"]',
    title: "Trip Voting",
    description: "Members propose dive trips and vote on dates. When you vote, a private chat room opens so you can plan together.",
    icon: Vote,
    position: "bottom",
    navigateTo: "/community",
    fallbackPosition: { top: 280, left: 100 }
  },
  {
    target: '[data-testid="community-marine-db-btn"]',
    title: "Marine Database",
    description: "Browse and contribute to our shared species database. Submit sightings with photos to help identify marine life.",
    icon: Fish,
    position: "bottom",
    navigateTo: "/community"
  },
  {
    target: '[data-testid="community-forum-btn"]',
    title: "Community Forum",
    description: "Discuss anything diving-related — gear reviews, site recommendations, trip stories, and more.",
    icon: MessageSquare,
    position: "bottom",
    navigateTo: "/community"
  },
  {
    target: '[data-testid="logo"]',
    title: "Dive Reports",
    description: "After a dive, log your report with details like depth, conditions, and sightings. Find it under 'Dive Report' in the navigation or your account.",
    icon: FileText,
    position: "bottom",
    navigateTo: "/dive-report"
  },
  {
    target: '[data-testid="floating-chat-btn"]',
    title: "Live Chat",
    description: "Chat with the community in real-time. Trip-specific rooms appear automatically when you vote on a trip. You're all set!",
    icon: MessageCircle,
    position: "top-left",
    navigateTo: "/",
    fallbackPosition: { bottom: 140, right: 16 }
  }
];

const NewUserWalkthrough = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [isNavigating, setIsNavigating] = useState(false);
  const tooltipRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initial launch check (+ re-check on login so no refresh is needed)
  useEffect(() => {
    let timer = null;
    const check = () => {
      const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (!userData) return;
      const user = JSON.parse(userData);
      if (user.walkthrough_completed) return;
      const walkthroughKey = `walkthrough_completed_${user.id}`;
      if (localStorage.getItem(walkthroughKey)) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsVisible(true), 1500);
    };
    check();
    window.addEventListener("userLogin", check);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("userLogin", check);
    };
  }, []);

  // Listen for restart event from Account page
  useEffect(() => {
    const handleRestart = () => {
      setCurrentStep(0);
      setIsNavigating(false);
      // Navigate to home first, then show
      navigate("/");
      setTimeout(() => setIsVisible(true), 800);
    };
    window.addEventListener('restartWalkthrough', handleRestart);
    return () => window.removeEventListener('restartWalkthrough', handleRestart);
  }, [navigate]);

  const positionTooltip = useCallback(() => {
    const step = WALKTHROUGH_STEPS[currentStep];
    if (!step || !isVisible || isNavigating) return;

    const target = document.querySelector(step.target);
    const tooltipWidth = 340;
    const tooltipHeight = 200;
    
    if (target) {
      const rect = target.getBoundingClientRect();
      let top, left;
      
      switch (step.position) {
        case "bottom":
          top = rect.bottom + 12;
          left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
          break;
        case "top":
          top = rect.top - tooltipHeight - 12;
          left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
          break;
        case "top-center":
          top = Math.max(16, rect.top + 20);
          left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
          break;
        case "top-left":
          top = rect.top - tooltipHeight - 12;
          left = Math.max(16, rect.right - tooltipWidth);
          break;
        default:
          top = rect.bottom + 12;
          left = Math.max(16, rect.left);
      }
      
      // Clamp within viewport
      top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));
      left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
      
      setTooltipPos({ top, left });
    } else if (step.fallbackPosition) {
      setTooltipPos({
        top: step.fallbackPosition.bottom ? window.innerHeight - step.fallbackPosition.bottom : step.fallbackPosition.top || 100,
        left: step.fallbackPosition.right ? window.innerWidth - step.fallbackPosition.right - tooltipWidth : step.fallbackPosition.left || 16
      });
    }
  }, [currentStep, isVisible, isNavigating]);

  useEffect(() => {
    if (!isVisible || isNavigating) return;
    // Re-position after a short delay to let the DOM settle
    const timer = setTimeout(positionTooltip, 100);
    window.addEventListener("resize", positionTooltip);
    window.addEventListener("scroll", positionTooltip);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", positionTooltip);
      window.removeEventListener("scroll", positionTooltip);
    };
  }, [isVisible, currentStep, isNavigating, positionTooltip]);

  // When step changes, navigate if needed
  useEffect(() => {
    if (!isVisible) return;
    const step = WALKTHROUGH_STEPS[currentStep];
    if (!step) return;
    
    if (step.navigateTo && location.pathname !== step.navigateTo) {
      setIsNavigating(true);
      navigate(step.navigateTo);
      // Wait for page to load
      setTimeout(() => {
        setIsNavigating(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 800);
    } else {
      // Already on the right page, scroll target into view
      setTimeout(() => {
        const target = document.querySelector(step.target);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);
    }
  }, [currentStep, isVisible, navigate, location.pathname]);

  const handleNext = () => {
    if (currentStep < WALKTHROUGH_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeWalkthrough();
    }
  };

  const completeWalkthrough = () => {
    setIsVisible(false);
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      const user = JSON.parse(userData);
      localStorage.setItem(`walkthrough_completed_${user.id}`, "true");
      const store = localStorage.getItem("user") ? localStorage : sessionStorage;
      store.setItem("user", JSON.stringify({ ...user, walkthrough_completed: true }));
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        axios.put(`${API}/users/me/walkthrough-complete`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    }
    // Navigate home after completing
    navigate("/");
  };

  if (!isVisible || isNavigating) return null;

  const step = WALKTHROUGH_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === WALKTHROUGH_STEPS.length - 1;

  return createPortal(
    <>
      {/* Overlay - clicking does NOT close */}
      <div 
        className="fixed inset-0 bg-black/30 z-[10200] transition-opacity duration-300"
        data-testid="walkthrough-overlay"
      />
      
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[10201] w-[340px] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{ top: `${tooltipPos.top}px`, left: `${tooltipPos.left}px` }}
        data-testid="walkthrough-tooltip"
      >
        {/* Header accent */}
        <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
        
        {/* Close button */}
        <button
          onClick={completeWalkthrough}
          className="absolute top-2.5 right-2.5 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
          data-testid="walkthrough-close-btn"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="p-4">
          {/* Icon + Title */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-cyan-600" />
            </div>
            <h3 className="font-semibold text-gray-900 text-base">{step.title}</h3>
          </div>
          
          {/* Description */}
          <p className="text-sm text-gray-600 leading-relaxed ml-12">{step.description}</p>
          
          {/* Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            {/* Step indicator */}
            <div className="flex items-center gap-1.5">
              {WALKTHROUGH_STEPS.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentStep ? "bg-cyan-500 w-3" : idx < currentStep ? "bg-cyan-300" : "bg-gray-200"}`} 
                />
              ))}
              <span className="text-xs text-gray-400 ml-1">{currentStep + 1}/{WALKTHROUGH_STEPS.length}</span>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <button 
                onClick={completeWalkthrough}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
                data-testid="walkthrough-skip-btn"
              >
                Skip tour
              </button>
              <button 
                onClick={handleNext}
                className="flex items-center gap-1 px-3 py-1.5 bg-cyan-500 text-white text-sm font-medium rounded-lg hover:bg-cyan-600 transition-colors"
                data-testid="walkthrough-next-btn"
              >
                {isLastStep ? "Got it!" : "Next"}
                {!isLastStep && <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default NewUserWalkthrough;
